"use client"

import { useCallback, type ReactNode } from "react"
import Link from "next/link"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Toaster } from "@/components/ui/sonner"
import {
  ToastSelectionContext,
  useToastSelectionClearance,
} from "@/components/ui/toast-selection-clearance"
import { ErrorIcon, InfoIcon, SuccessIcon } from "@/icons"

type ToastActionLink = {
  text: string
} & ({ href: string; onClick?: never } | { href?: never; onClick: () => void })
interface RhythmsToastOptions {
  title: ReactNode
  description?: ReactNode
  type: "success" | "error" | "info" | "loading" | "default"
  promise?: Promise<unknown>
  endContent?: ReactNode
  actionLink?: ToastActionLink
  /**
   * Optional second action rendered inline next to `actionLink` (same text-link
   * styling). Absent → the toast renders exactly as before with a single link.
   */
  secondaryActionLink?: ToastActionLink
  timeout?: number
  shouldShowTimeoutProgress?: boolean
  // Add other custom options if needed
}

// Overload signatures for showToast. Returns the sonner toast id so callers
// can dismiss the toast imperatively (e.g. dismiss an advisory toast the
// moment its action is taken).
interface ShowToast {
  (options: RhythmsToastOptions): string | number
  (message: string, type: RhythmsToastOptions["type"]): string | number
}

// Using FluentUI icons as per guidelines
const typeToIconMap: Record<
  RhythmsToastOptions["type"],
  ReactNode | undefined
> = {
  success: <SuccessIcon className="text-success size-4" />,
  error: <ErrorIcon className="text-destructive size-4" />,
  info: <InfoIcon className="text-brand size-4" />,
  loading: undefined,
  // Sonner handles loading icon automatically
  default: undefined,
}
export function useToast() {
  const showToast: ShowToast = useCallback(
    (
      optionsOrMessage: RhythmsToastOptions | string,
      typeArg?: RhythmsToastOptions["type"]
    ) => {
      const options: RhythmsToastOptions =
        typeof optionsOrMessage === "string"
          ? {
              title: optionsOrMessage,
              type: typeArg ?? "default",
            }
          : optionsOrMessage
      const {
        title,
        description,
        type,
        actionLink,
        secondaryActionLink,
        timeout,
      } = options

      // Process title - handle newline splitting like before
      let finalTitle = title
      let finalDescription: ReactNode | undefined = description
      if (typeof title === "string" && !description) {
        const lines = title.split("\n")
        if (lines.length > 1) {
          finalTitle = lines[0]
          finalDescription = lines.slice(1).join("\n")
        }
      }

      // Build a single action link element from a ToastActionLink.
      const buildActionLinkElement = (link: ToastActionLink): ReactNode => {
        if (link.href !== undefined) {
          const internal =
            link.href.startsWith("/") && !link.href.startsWith("//")
          return (
            <Button
              variant="link-brand"
              render={
                internal ? (
                  <Link href={link.href}>{link.text}</Link>
                ) : (
                  <a href={link.href} target="_blank" rel="noopener noreferrer">
                    {link.text}
                  </a>
                )
              }
            >
              {link.text}
            </Button>
          )
        }
        return (
          <Button variant="link-brand" onClick={link.onClick}>
            {link.text}
          </Button>
        )
      }

      // Render one or two action links inline in a row when provided.
      const actionLinksElement =
        actionLink || secondaryActionLink ? (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {actionLink ? buildActionLinkElement(actionLink) : null}
            {secondaryActionLink
              ? buildActionLinkElement(secondaryActionLink)
              : null}
          </div>
        ) : null

      // Build description with action link at bottom
      const descriptionWithAction =
        finalDescription || actionLinksElement ? (
          <>
            {finalDescription ? <div>{finalDescription}</div> : null}
            {actionLinksElement}
          </>
        ) : undefined

      // Build Sonner options
      const sonnerOptions: Parameters<typeof toast>[1] = {
        description: descriptionWithAction,
        duration: timeout ?? (type === "error" ? 10_000 : 6000),
        icon: typeToIconMap[type],
      }

      // Call appropriate Sonner method based on type
      switch (type) {
        case "success":
          return toast.success(finalTitle, sonnerOptions)
        case "error":
          return toast.error(finalTitle, sonnerOptions)
        case "info":
          return toast.info(finalTitle, sonnerOptions)
        case "loading":
          return toast.loading(finalTitle, sonnerOptions)
        case "default":
          return toast(finalTitle, sonnerOptions)
      }
    },
    []
  )

  const dismissToast = (id: string | number) => {
    toast.dismiss(id)
  }

  /**
   * Shows a toast that can be updated in place.
   * Returns an `update` function to change the toast content.
   */
  const showUpdatableToast = (initialOptions: {
    title: ReactNode
    description?: ReactNode
    type?: RhythmsToastOptions["type"]
    timeout?: number
  }) => {
    // Create the initial loading toast and get its ID
    const toastId = toast.loading(initialOptions.title, {
      description: initialOptions.description,
      duration: initialOptions.timeout ?? Infinity,
      // Updatable toasts should persist until updated
      icon: typeToIconMap[initialOptions.type ?? "loading"],
    })
    const update = (newOptions: {
      title: ReactNode
      description?: ReactNode
      type?: RhythmsToastOptions["type"]
    }) => {
      const updateOptions = {
        id: toastId,
        description: newOptions.description,
        icon: typeToIconMap[newOptions.type ?? "default"],
        duration: 6000, // Reset to normal duration after update
      }

      // Use the appropriate toast method based on the new type
      switch (newOptions.type) {
        case "success":
          toast.success(newOptions.title, updateOptions)
          break
        case "error":
          toast.error(newOptions.title, updateOptions)
          break
        case "info":
          toast.info(newOptions.title, updateOptions)
          break
        case "loading":
          toast.loading(newOptions.title, {
            ...updateOptions,
            duration: Infinity,
          })
          break
        case "default":
        case undefined:
          toast(newOptions.title, updateOptions)
          break
      }
    }
    return {
      update,
    }
  }
  return {
    showToast,
    dismissToast,
    showUpdatableToast,
  }
}
export function RhythmsToastProvider({ children }: { children: ReactNode }) {
  const { bottom, register } = useToastSelectionClearance()
  return (
    <ToastSelectionContext value={register}>
      <Toaster
        position="bottom-right"
        closeButton
        offset={{ bottom, left: 16, right: 16, top: 16 }}
        mobileOffset={{ bottom, left: 16, right: 16, top: 16 }}
        visibleToasts={5}
        theme="light"
      />
      {children}
    </ToastSelectionContext>
  )
}
